import React, { useState, useEffect } from 'react';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, CardHeader, Card, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import CheckIcon from '@mui/icons-material/Check';
import LockIcon from '@mui/icons-material/Lock';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TableSortLabel from '@mui/material/TableSortLabel';
import VideoSensorBackIcon from '@mui/icons-material/VideoCameraBack';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import LinkedSensorIcon from '@mui/icons-material/LinkedCamera';
import PhotoSensorIcon from '@mui/icons-material/PhotoCamera';
import LinearProgress from '@mui/material/LinearProgress';
import EventIcon from '@mui/icons-material/Event';
import TablePagination from '@mui/material/TablePagination';
import axiosInstance from '../../../utils/axiosInstance';

// eslint-disable-next-line
const SensorTable = ({ host }) => {
  const [loading, setLoading] = useState(true);
  const [sensorData, setSensorData] = useState([]);
  const [sensorStatus, setSensorStatus] = useState({});
  const [sensorRecordingStatus, setSensorRecordingStatus] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('remoteDeviceLocation');
  const [order, setOrder] = useState('asc');
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [sensorListResponse, sensorStatusResponse, recordStatusResponse] = await Promise.all([
          axiosInstance.get(`${window.location.protocol}//${host}/api/v1/sensor/list`),
          axiosInstance.get(`${window.location.protocol}//${host}/api/v1/sensor/status`),
          axiosInstance.get(`${window.location.protocol}//${host}/api/v1/record/status`)
        ]);

        if (sensorListResponse.data) {
          const filteredList = sensorListResponse.data.filter((device) => !(Object.prototype.hasOwnProperty.call(device, 'state') && device.state === 'removed'));
          setSensorData(filteredList);
        }

        if (sensorStatusResponse.data) {
          setSensorStatus(sensorStatusResponse.data);
        }

        if (recordStatusResponse.data) {
          setSensorRecordingStatus(recordStatusResponse.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        enqueueSnackbar(`Error - Failed to fetch sensor data`, {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [host, enqueueSnackbar]);

  const handleSortRequest = (columnId) => {
    const isAsc = orderBy === columnId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
    setPage(0);
  };

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
  };

  // eslint-disable-next-line
  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  const getStatus = (sensorId) => {
    const status = sensorStatus[sensorId];
    return status ? status.errorCode : 'unknown';
  };

  const getRecordingStatus = (sensorId) => {
    const status = sensorRecordingStatus[sensorId];
    return status ? status.recording_status : 'unknown';
  };

  const getTableCell = (val) => {
    const iconMap = {
      SensorUnauthorizedError: <LockIcon sx={{ color: 'error.main' }} />,
      NoError: <CheckIcon sx={{ color: 'secondary.main' }} />,
      error: <ErrorOutlineIcon sx={{ color: 'error.main' }} />,
      statusUnknown: <ErrorOutlineIcon sx={{ color: 'error.main' }} />,
      user: <VideoSensorBackIcon sx={{ color: 'secondary.main' }} />,
      schedule: <VideoSensorBackIcon sx={{ color: 'secondary.main' }} />,
      alwaysOn: <VideoSensorBackIcon sx={{ color: 'secondary.main' }} />,
      off: <VideocamOffIcon sx={{ color: 'error.main' }} />,
      event: <EventIcon sx={{ color: 'secondary.main' }} />
    };

    return (
      <Tooltip title={val}>
        {iconMap[val] || <ErrorOutlineIcon sx={{ color: 'error.main' }} />}
      </Tooltip>
    );
  };

  const sortedData = stableSort(sensorData, getComparator(order, orderBy));
  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Card variant="outlined">
      <CardHeader title="Sensor list" subheader="List of connected sensors" />
      {loading ? (
        <LinearProgress color="inherit" sx={{ m: 2 }} />
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {['name', 'status', 'recordingStatus', 'remoteDeviceName', 'remoteDeviceLocation'].map((column) => (
                    <TableCell key={column}>
                      <TableSortLabel
                        active={orderBy === column}
                        direction={orderBy === column ? order : 'asc'}
                        onClick={() => handleSortRequest(column)}
                      >
                        {column === 'name' ? 'Sensor Name' :
                          column === 'status' ? 'Sensor Status' :
                            column === 'recordingStatus' ? 'Sensor Recording Status' :
                              column === 'remoteDeviceName' ? 'Device Name' : 'Device Location'}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((sensor) => (
                  <TableRow key={sensor.sensorId}>
                    <TableCell sx={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ marginRight: '8px' }}>{sensor.name}</span>
                      {sensor.isRemoteSensor ? <LinkedSensorIcon sx={{ color: 'secondary.main' }} /> : <PhotoSensorIcon sx={{ color: 'secondary.main' }} />}
                    </TableCell>
                    <TableCell>{getTableCell(getStatus(sensor.sensorId))}</TableCell>
                    <TableCell>{getTableCell(getRecordingStatus(sensor.sensorId))}</TableCell>
                    <TableCell>{sensor.remoteDeviceName}</TableCell>
                    <TableCell>{sensor.remoteDeviceLocation}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10]}
            component="div"
            count={sortedData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </>
      )}
    </Card>
  );
};

export default SensorTable;